import React, { type ReactNode, useCallback, useEffect, useState } from 'react';

import noop from 'lodash/noop';
import Button from '@atlaskit/button';
import PreferencesIcon from '@atlaskit/icon/glyph/preferences';
import { Box, xcss } from '@atlaskit/primitives';
import { Show } from '@atlaskit/primitives/responsive';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ff } from '@atlassian/jira-feature-flagging';
import { useNextBestTaskActions } from '@atlassian/jira-insights-next-best-task';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useGlobalRefStoreActions } from '@atlassian/jira-refs-store';
import { ResponsiveButton } from '@atlassian/jira-responsive-button';
import {
	VIEW_SETTINGS_BUTTON_LG,
	VIEW_SETTINGS_BUTTON_SM,
	VIEW_SETTINGS_BUTTON_RESPONSIVE,
} from '../../common/constants';
import type { View } from '../../common/types';
import type { ExtraField } from '../../common/types/menu';
import { useCardDetailsInitialState } from '../../controllers/hooks/use-card-details-initial-state';
import { useOnBacklog } from '../../controllers/hooks/use-on-backlog';
import { useSyncFromLocalStorage } from '../../controllers/hooks/use-sync-from-local-storage';
import messages from './messages';

type Props = {
	isSelected: boolean;
	/**
	 * All spacing should come from flex container `gap:` or primitive `space=`.
	 */
	removeMargins?: boolean;
	onClick: (isPanelOpen: boolean) => void;
	ShowDI?: typeof Show;
	ariaControls?: string;
	ariaExpanded?: boolean;
	ariaHasPopup?: boolean;
};

export const ViewSettingsSyncFromLocalStorageWrapper = ({
	boardId,
	view,
	cardExtraFields = [],
	isDaysInColumnEnabled = false,
	isDueDateEnabled = false,
	isLabelsEnabled = false,
	isEstimateEnabled = false,
	children,
	showEpicAsPanel = false,
	isVersionsEnabled = false,
	isPriorityEnabled = false,
}: {
	boardId: number;
	view: (typeof View)[keyof typeof View];
	children: ReactNode;
	cardExtraFields?: ExtraField[];
	isDaysInColumnEnabled?: boolean;
	isDueDateEnabled?: boolean;
	isLabelsEnabled?: boolean;
	showEpicAsPanel?: boolean;
	isVersionsEnabled?: boolean;
	isEstimateEnabled?: boolean;
	isPriorityEnabled?: boolean;
}) => {
	useOnBacklog(boardId, view, showEpicAsPanel, isVersionsEnabled);
	useSyncFromLocalStorage(
		boardId,
		view,
		cardExtraFields,
		isDaysInColumnEnabled,
		isDueDateEnabled,
		isLabelsEnabled,
		isEstimateEnabled,
		isPriorityEnabled,
	);
	useCardDetailsInitialState(boardId, view, isDaysInColumnEnabled);

	return <>{children}</>;
};

export const ViewSettingsButton = ({
	isSelected,
	onClick = noop,
	removeMargins,
	ShowDI = Show,
	...restProps
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(isSelected);
	const [, { register }] = useGlobalRefStoreActions();

	const handleClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'viewSettingsButton',
		);

		setIsOpen(!isOpen);
		onClick(!isOpen);
	}, [createAnalyticsEvent, isOpen, onClick]);

	const openViewSettings = useCallback(() => {
		if (!ff('jira.insights.work-suggestions-on-boarding-spotlight-view-settings_dzjgq') && isOpen) {
			return;
		}
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'viewSettingsButton',
		);

		setIsOpen(true);
		setTimeout(() => onClick(true));
	}, [createAnalyticsEvent, onClick, isOpen]);

	const { setOpenViewSettings } = useNextBestTaskActions();

	useEffect(() => {
		if (
			ff('jira.insights.work-suggestions-on-boarding-spotlight-view-settings_dzjgq') &&
			setOpenViewSettings
		) {
			setOpenViewSettings(openViewSettings);
		}
	}, [openViewSettings, setOpenViewSettings]);

	const onRefButtonLarge = useCallback(
		(ref: HTMLDivElement | null) => {
			register(VIEW_SETTINGS_BUTTON_LG, ref);
		},
		[register],
	);

	const onRefButtonSmall = useCallback(
		(ref: HTMLDivElement | null) => {
			register(VIEW_SETTINGS_BUTTON_SM, ref);
		},
		[register],
	);

	const onRefButtonResponsive = useCallback(
		(ref: HTMLDivElement | null) => {
			register(VIEW_SETTINGS_BUTTON_RESPONSIVE, ref);
		},
		[register],
	);

	useEffect(() => {
		setIsOpen(isSelected);
	}, [isSelected]);

	const iconColor = isSelected
		? token('color.icon.selected', colors.B400)
		: token('color.icon', colors.N300);

	if (ff('sea-2988-view-settings-aria-attribute-fix_p98hy')) {
		return (
			<>
				{ff('sea-2744-jsw-tabs-nav') ? (
					<ResponsiveButton
						testId="software-view-settings.ui.view-settings-button.responsive-button"
						icon={PreferencesIcon}
						label={formatMessage(messages.buttonLabel)}
						isSelected={isOpen}
						onClick={handleClick}
						ref={onRefButtonResponsive}
						aria-expanded={restProps.ariaExpanded ?? isSelected}
						aria-controls={restProps.ariaControls}
						aria-haspopup={restProps.ariaHasPopup ?? true}
					/>
				) : (
					<Box xcss={removeMargins ? undefined : buttonWrapperStyles}>
						<ShowDI above="lg">
							<Button
								testId="software-view-settings.ui.view-settings-button.large-button"
								appearance="default"
								isSelected={isOpen}
								onClick={handleClick}
								ref={onRefButtonLarge}
								iconBefore={
									<PreferencesIcon
										label={formatMessage(messages.buttonLabel)}
										primaryColor={iconColor}
									/>
								}
								aria-label={formatMessage(messages.buttonLabel)}
								aria-expanded={restProps.ariaExpanded ?? isSelected}
								aria-controls={restProps.ariaControls}
								aria-haspopup={restProps.ariaHasPopup ?? true}
							>
								{formatMessage(messages.buttonLabel)}
							</Button>
						</ShowDI>
						<ShowDI below="lg">
							<Tooltip content={formatMessage(messages.buttonLabel)}>
								<Button
									testId="software-view-settings.ui.view-settings-button.small-button"
									appearance="default"
									isSelected={isOpen}
									onClick={handleClick}
									spacing="none"
									ref={onRefButtonSmall}
									aria-expanded={restProps.ariaExpanded ?? isSelected}
									aria-controls={restProps.ariaControls}
									aria-haspopup={restProps.ariaHasPopup ?? true}
								>
									<Box xcss={iconWrapperStyles}>
										<PreferencesIcon
											label={formatMessage(messages.buttonLabel)}
											primaryColor={iconColor}
										/>
									</Box>
								</Button>
							</Tooltip>
						</ShowDI>
					</Box>
				)}
			</>
		);
	}

	return (
		<>
			{ff('sea-2744-jsw-tabs-nav') ? (
				<ResponsiveButton
					testId="software-view-settings.ui.view-settings-button.responsive-button"
					icon={PreferencesIcon}
					label={formatMessage(messages.buttonLabel)}
					isSelected={isOpen}
					onClick={handleClick}
					ref={onRefButtonResponsive}
				/>
			) : (
				<Box xcss={removeMargins ? undefined : buttonWrapperStyles}>
					<ShowDI above="lg">
						<Button
							testId="software-view-settings.ui.view-settings-button.large-button"
							appearance="default"
							isSelected={isOpen}
							onClick={handleClick}
							ref={onRefButtonLarge}
							iconBefore={
								<PreferencesIcon
									label={formatMessage(messages.buttonLabel)}
									primaryColor={iconColor}
								/>
							}
						>
							{formatMessage(messages.buttonLabel)}
						</Button>
					</ShowDI>
					<ShowDI below="lg">
						<Tooltip content={formatMessage(messages.buttonLabel)}>
							<Button
								testId="software-view-settings.ui.view-settings-button.small-button"
								appearance="default"
								isSelected={isOpen}
								onClick={handleClick}
								spacing="none"
								ref={onRefButtonSmall}
							>
								<Box xcss={iconWrapperStyles}>
									<PreferencesIcon
										label={formatMessage(messages.buttonLabel)}
										primaryColor={iconColor}
									/>
								</Box>
							</Button>
						</Tooltip>
					</ShowDI>
				</Box>
			)}
		</>
	);
};

const iconWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '32px',
	height: '32px',
});

const buttonWrapperStyles = xcss({
	marginLeft: 'space.100',
});
